import { Box, Stack, Tooltip, Typography, useTheme, Zoom, Grid, Button } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import HomeOutlinedIcon from '@mui/icons-material/HomeRounded'
import ReadMoreIcon from '@mui/icons-material/ReadMore'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { ReactComponent as FairLogo } from '../../assets/logos/FS_small.svg'
import { ReactComponent as PTLogo } from '../../assets/logos/PT_small.svg'
import { ReactComponent as ZELogo } from '../../assets/logos/Zert_Equal.svg'
import { ReactComponent as CWLogo } from '../../assets/logos/Compass_W.svg'
import { ReactComponent as ZELogoDark } from '../../assets/logos/ZE_dark.svg'
import { ReactComponent as CWLogoDark } from '../../assets/logos/CW_dark.svg'
import MinistryEnLogo from '../../assets/logos/Ministry_en.png'
import MinistryDeLogo from '../../assets/logos/Ministry_de.png'
import { ReactComponent as EULogo } from '../../assets/logos/EU.svg'
import { askForAccess } from '../../store/actions'

const FsHome = () => {
  const { account, currentTheme, currentLanguage } = useAppSelector((state) => state.currentUser)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { t } = useTranslation()

  const slides = [
    {
      logo:
        currentTheme === 'dark' ? (
          <ZELogoDark height="40px" width="160px" />
        ) : (
          <ZELogo height="40px" width="160px" />
        ),
      description: t('ze.desc'),
      image: (
        <Box
          sx={{
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'white',
            display: 'inline-flex',
            padding: 1,
            backgroundColor: 'white',
            width: 'fit-content',
          }}
        >
          <img
            src={currentLanguage === 'en' ? MinistryEnLogo : MinistryDeLogo}
            alt="mimistry logo"
            width="auto"
            height={140}
            style={{ objectFit: 'contain' }}
          />
        </Box>
      ),
      link: `https://www.ines-analytics.com/${currentLanguage === 'en' ? 'en/' : ''}projekte`,
    },
    {
      logo:
        currentTheme === 'dark' ? (
          <CWLogoDark height="50px" width="220px" />
        ) : (
          <CWLogo height="50px" width="220px" />
        ),
      description: t('cw.desc'),
      image: (
        <Box
          sx={{
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'white',
            display: 'inline-flex',
            padding: 1,
            backgroundColor: 'white',
            width: 'fit-content',
          }}
        >
          <img
            src={currentLanguage === 'en' ? MinistryEnLogo : MinistryDeLogo}
            alt="mimistry logo"
            width="auto"
            height={140}
            style={{ objectFit: 'contain' }}
          />
        </Box>
      ),
      image2: (
        <Box
          sx={{
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'white',
            display: 'inline-flex',
            padding: 1,
            backgroundColor: 'white',
          }}
        >
          <EULogo height="auto" width={150} />
        </Box>
      ),
      link: `https://www.ines-analytics.com/${currentLanguage === 'en' ? 'en/' : ''}projekte`,
    },
  ]

  return (
    <Stack mt={4}>
      <Grid container spacing={4}>
        <Grid size={{ sm: 12, md: 12, lg: 6 }}>
          <Stack
            className="p-4 items-start justify-center rounded-lg"
            boxShadow={`2px 2px 8px 2px ${theme.palette.action.selected}`}
          >
            <Stack direction="row" spacing={1} className="mb-2 justify-center items-end">
              <HomeOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h1" sx={{ lineHeight: 1.1 }}>
                Home
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Stack
                p={2}
                className="pl-1"
                alignItems="flex-start"
                justifyContent="top"
                border={`1px ${theme.palette.secondary.light}`}
                borderRadius={2}
                direction="column"
              >
                <Typography
                  variant="h2"
                  className="font-semibold"
                  color={theme.palette.primary.main}
                >
                  {`${t('home.title')}${
                    account?.firstName ? `, ${account?.firstName} ${account?.lastName}` : ''
                  }!`}
                </Typography>
                <Typography variant="body1" mt={2}>
                  {t('home.desc')}
                </Typography>
              </Stack>
              <Stack direction="row" width="100%" spacing={2}>
                <Tooltip title={<Typography variant="body2">{t('pt.tagline')}</Typography>}>
                  <Stack
                    onClick={() =>
                      account?.profile.appAccess?.includes('pt')
                        ? navigate('/pt/home')
                        : dispatch(askForAccess('pt'))
                    }
                    p={2}
                    alignItems="center"
                    justifyContent="center"
                    borderRadius={2}
                    width="100%"
                    height="100%"
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: theme.palette.action.hover,
                      backdropFilter: 'blur(8px)',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <PTLogo height="90px" width="100px" />
                    <Typography mt={2} variant="h2">
                      PAY_transparency
                    </Typography>
                    {!account?.profile.appAccess?.includes('pt') && (
                      <Box
                        mt={1}
                        p={1}
                        py={0.25}
                        sx={{ backgroundColor: theme.palette.primary.light, borderRadius: 1 }}
                      >
                        <Typography variant="body2" className="font-semibold">
                          {t('home.try')}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Tooltip>
                <Tooltip title={<Typography variant="body2">{t('fs.tagline')}</Typography>}>
                  <Stack
                    onClick={() =>
                      account?.profile.appAccess?.includes('fs')
                        ? navigate('/fs/dashboard')
                        : dispatch(askForAccess('fs'))
                    }
                    p={2}
                    alignItems="center"
                    justifyContent="center"
                    borderRadius={2}
                    width="100%"
                    height="100%"
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: theme.palette.action.hover,
                      backdropFilter: 'blur(8px)',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <FairLogo height="90px" width="100px" />
                    <Typography mt={2} ml={2} variant="h2">
                      FAIR_solution
                    </Typography>
                    {!account?.profile.appAccess?.includes('fs') && (
                      <Box
                        mt={1}
                        p={1}
                        py={0.25}
                        sx={{ backgroundColor: theme.palette.primary.light, borderRadius: 1 }}
                      >
                        <Typography variant="body2" className="font-semibold">
                          {t('home.try')}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={4} mt={4}>
        <Grid size={{ sm: 12 }}>
          <Stack
            p={4}
            alignItems="flex-start"
            justifyContent="flex-start"
            boxShadow={`2px 2px 8px 2px ${theme.palette.action.selected}`}
            borderRadius={2}
          >
            <Stack direction="row" spacing={1} mb={2} alignItems="flex-start">
              <ReadMoreIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h1">{t('home.more.projects')}</Typography>
            </Stack>
            <Stack
              direction="column"
              alignItems="flex-start"
              justifyContent="flex-start"
              spacing={2}
              mt={2}
            >
              <Stack direction={{ sm: 'column', md: 'column', lg: 'row' }} width="100%" spacing={2}>
                <Stack
                  p={4}
                  alignItems="center"
                  justifyContent="flex-start"
                  borderRadius={2}
                  width="100%"
                  sx={{
                    backgroundColor: theme.palette.action.hover,
                  }}
                >
                  <Stack justifyContent="space-between" alignItems="center" height="100%">
                    <Box>
                      <Typography variant="h1">{slides[0].logo}</Typography>
                      <Typography my={3}>{slides[0].description}</Typography>
                    </Box>
                    <Stack className="justify-center items-center">
                      {slides[0].image}
                      <Button
                        variant="contained"
                        endIcon={<OpenInNewIcon />}
                        href={slides[0].link}
                        target="_blank"
                        sx={{ mt: 2 }}
                      >
                        {t('read.more.ines')}
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack
                  p={4}
                  alignItems="center"
                  justifyContent="flex-start"
                  borderRadius={2}
                  width="100%"
                  sx={{
                    backgroundColor: theme.palette.action.hover,
                  }}
                >
                  <Stack justifyContent="space-between" alignItems="center" height="100%">
                    <Box>
                      <Typography variant="h3">{slides[1].logo}</Typography>
                      <Typography my={2}>{slides[1].description}</Typography>
                    </Box>
                    <Stack className="justify-center items-center">
                      <Stack direction="row" spacing={2}>
                        {slides[1].image}
                        {slides[1].image2}
                      </Stack>
                      <Button
                        variant="contained"
                        endIcon={<OpenInNewIcon />}
                        href={slides[1].link}
                        target="_blank"
                        sx={{ mt: 2 }}
                      >
                        {t('read.more.ines')}
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  )
}

export default FsHome
